import {
  useAtomWithErrorNotification,
  useWithErrorNotification,
} from 'shared/ui/GlobalDialog'
import { allCrosswalksAtom, allFrameworksAtom } from './atoms'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { CrosswalkMappingSetType, FrameworkType } from './types'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import { newCrosswalkDataAtom } from 'domains/scope-and-sequence/features/crosswalk/atoms'
import { createCrosswalkMappingSet, getCrosswalkById } from './api'
import { isCompleteCrosswalk } from './utility'
import { objectFieldSort } from 'shared/utils'

export const CommonCoreJurisdiction = 'CCK12'

export function useAllFrameworks() {
  return useAtomWithErrorNotification(
    { errorTitle: 'Error Retrieving Frameworks ' },
    allFrameworksAtom,
  )?.sort(objectFieldSort('name'))
}

export function useCommonCoreFrameworks() {
  const allFrameworks = useAllFrameworks()
  return useMemo(() => {
    return allFrameworks?.filter(
      (f) => f.jurisdiction === CommonCoreJurisdiction,
    )
  }, [allFrameworks])
}

export function useFrameworksById() {
  const frameworks = useAllFrameworks()
  return useMemo(() => {
    return frameworks?.reduce<Record<number, FrameworkType>>(
      (acc, f) => ({
        ...acc,
        [f.id]: f,
      }),
      {},
    )
  }, [frameworks])
}

export function useGetFrameworkById() {
  const frameworks = useFrameworksById()
  return useCallback(
    (id: string | number) => frameworks?.[Number(id)],
    [frameworks],
  )
}

export function useFrameworkForId(id: string | number | undefined) {
  const getter = useGetFrameworkById()
  if (!id) return
  return getter(id)
}

export function useAllCrosswalks() {
  return useAtomWithErrorNotification(
    { errorTitle: 'Error Retrieving Crosswalks ' },
    allCrosswalksAtom,
  )
}

/**
 * Fetches a single crosswalk mapping set by ID.
 * @param id - The crosswalk mapping set ID
 * @returns Object containing crosswalk data and a refetch function
 */
export function useCrosswalk(id: number) {
  const [crosswalk, setCrosswalk] = useState<
    CrosswalkMappingSetType | undefined
  >()
  const [fetchCount, setFetchCount] = useState(0)

  useEffect(() => {
    let cancelled = false
    setCrosswalk(undefined)
    getCrosswalkById(id).then(({ result }) => {
      if (!cancelled) {
        setCrosswalk(result)
      }
    })
    return () => {
      cancelled = true
    }
  }, [id, fetchCount])

  const refetch = useCallback(() => setFetchCount((c) => c + 1), [])

  return { crosswalk, refetch }
}

export function useCreateNewCrosswalk() {
  const data = useAtomValue(newCrosswalkDataAtom)
  const doCreate = useWithErrorNotification(
    { errorTitle: 'Error Creating Crosswalk' },
    createCrosswalkMappingSet,
  )
  const refreshCrosswalks = useSetAtom(allCrosswalksAtom)

  return useCallback(async () => {
    if (!isCompleteCrosswalk(data)) return
    await doCreate(data)
    refreshCrosswalks()
  }, [data, doCreate, refreshCrosswalks])
}
